/**
 * The Support Inbox's right panel for one ticket: header (user, status line,
 * Claim / Unclaim / Resolve), the thread, and the composer — or, when the
 * viewer can't reply, why not.
 *
 * The ticket is read from GET /chat/rooms/{id}, not from the inbox list, so
 * it stays on screen whatever the filter shows — e.g. after claiming it from
 * "Unclaimed". It is re-read after each action (success or 409: the claim
 * may have changed under the click) and on every ticket_* frame, since
 * those are what change a ticket's status or assignee.
 *
 * The ticket's room is held as INBOX_HOLDER while it is selected. Switching
 * tickets releases the previous room before holding the next (the effect's
 * cleanup runs first), and deselecting releases it too, so the inbox never
 * keeps a room joined that nothing shows.
 *
 * Which buttons show, the status line, and whether the composer shows all
 * come from ticketState.ts.
 *
 * A scoped supporter (chat.support without chat.support.all) can lose access
 * while looking at a ticket — someone else claims it. The server then sends
 * `ticket_removed`, the re-read answers 403 `out_of_scope`, and the detail
 * calls `onGone` so the inbox can close it, instead of showing an error. Any
 * other 403 (`missing_capability`: the viewer is no longer support staff)
 * is not "taken by someone else": it shows an access message instead.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/api/client";
import { CAP } from "@/api/capabilities";
import type { ChatInboxRoom, ChatMessage } from "@/api/types";
import { ChatComposer } from "@/components/Chat/ChatComposer";
import { ChatThread } from "@/components/Chat/ChatThread";
import { errMsg } from "@/components/Chat/chatErrors";
import { useChat, useHeldRoom, useRoomViewing } from "@/hooks/useChatSocket";
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { pushToast } from "@/state/slices/notificationsSlice";
import { initials } from "@/utils/format";
import { tintFor } from "./TicketList";
import { useSettledTicketEventSeq } from "./ticketEvents";
import { canReply, composerHint, ticketActions, ticketStatusText, type TicketActions } from "./ticketState";
import shared from "../Chat/ChatRoom.module.css";
import styles from "./TicketDetail.module.css";

/** This component's name as a room holder (see chatSlice `roomHolders`). */
export const INBOX_HOLDER = "inbox";

type Action = keyof TicketActions;

const ACTIONS: { key: Action; label: string; call: (roomId: string) => Promise<unknown> }[] = [
  { key: "claim", label: "Claim", call: api.chatClaim },
  { key: "unclaim", label: "Unclaim", call: api.chatUnclaim },
  { key: "resolve", label: "Resolve", call: api.chatResolve },
];

type Loaded = {
  roomId: string;
  ticket: ChatInboxRoom | null;
  error: string | null;
  /** The server no longer lets the viewer see it (403 out_of_scope). */
  gone: boolean;
  /** Any other 403: the viewer lost access to support tickets altogether. */
  denied: boolean;
};

/** Out of the viewer's scope — e.g. another supporter claimed it. */
const isGone = (e: unknown) =>
  e instanceof ApiError && e.status === 403 && e.code === "out_of_scope";
/** A 403 for any other reason (a lost capability). */
const isDenied = (e: unknown) => e instanceof ApiError && e.status === 403 && !isGone(e);

/** The ticket from the server: on selection, after a burst of ticket_*
 *  frames settles (one read per burst), and on `refetch()`. Only the newest
 *  response lands; a failed re-read keeps the last known ticket. */
function useTicket(roomId: string): {
  ticket: ChatInboxRoom | null;
  error: string | null;
  gone: boolean;
  denied: boolean;
  refetch: () => void;
} {
  const eventSeq = useSettledTicketEventSeq();
  const [loaded, setLoaded] = useState<Loaded>({
    roomId,
    ticket: null,
    error: null,
    gone: false,
    denied: false,
  });
  const [attempt, setAttempt] = useState(0);
  const latestRef = useRef(0);

  useEffect(() => {
    const req = (latestRef.current += 1);
    api
      .chatTicket(roomId)
      .then((ticket) => {
        if (req === latestRef.current) {
          setLoaded({ roomId, ticket, error: null, gone: false, denied: false });
        }
      })
      .catch((e: unknown) => {
        if (req !== latestRef.current) return;
        if (isGone(e) || isDenied(e)) {
          setLoaded({ roomId, ticket: null, error: null, gone: isGone(e), denied: isDenied(e) });
          return;
        }
        setLoaded((prev) => ({
          roomId,
          ticket: prev.roomId === roomId ? prev.ticket : null,
          error: errMsg(e),
          gone: false,
          denied: false,
        }));
      });
  }, [roomId, eventSeq, attempt]);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);
  const current = loaded.roomId === roomId;
  return {
    ticket: current ? loaded.ticket : null,
    error: current ? loaded.error : null,
    gone: current && loaded.gone,
    denied: current && loaded.denied,
    refetch,
  };
}

export function TicketDetail({
  ticketId: roomId,
  onGone,
}: {
  ticketId: string;
  /** The ticket left the viewer's scope (the server now answers 403). */
  onGone?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { joinRoom, leaveRoom } = useChat();
  const viewerSub = useAppSelector((s) => s.me.sub);
  // chat.support.all: may unclaim / resolve tickets that aren't the viewer's.
  const fullAccess = useCan(CAP.chatSupportAll);
  const { ticket, error, gone, denied, refetch } = useTicket(roomId);
  // Out of scope (or removed) while open: hand back to the inbox.
  const removed = useAppSelector((s) => s.chat.removedTicketIds.includes(roomId));
  useEffect(() => {
    if (gone || removed) onGone?.();
  }, [gone, removed, onGone]);

  const { held } = useHeldRoom(roomId, INBOX_HOLDER);
  useRoomViewing(held ? roomId : null);

  // The room we asked to hold: not held after that means the join was refused.
  const joinedRef = useRef<string | null>(null);
  useEffect(() => {
    joinedRef.current = roomId;
    joinRoom(roomId, INBOX_HOLDER);
    return () => leaveRoom(roomId, INBOX_HOLDER);
  }, [roomId, joinRoom, leaveRoom]);
  const refused = joinedRef.current === roomId && !held;

  const [busy, setBusy] = useState<{ roomId: string; action: Action } | null>(null);
  const busyHere = busy?.roomId === roomId;

  const run = async (action: (typeof ACTIONS)[number]) => {
    setBusy({ roomId, action: action.key });
    try {
      await action.call(roomId);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        dispatch(
          pushToast({
            kind: "warning",
            title: `Couldn't ${action.label.toLowerCase()} — the ticket changed`,
            message: "Showing its current state.",
          }),
        );
      } else {
        dispatch(pushToast({ kind: "error", title: `${action.label} failed`, message: errMsg(e) }));
        return;
      }
    } finally {
      setBusy((b) => (b?.roomId === roomId ? null : b));
    }
    refetch();
  };

  if (denied) {
    return (
      <div className={styles.detail}>
        <div className={styles.connecting}>
          <p className={shared.threadNote} role="alert">
            You no longer have access to support tickets. Reload the page if your role
            just changed.
          </p>
        </div>
      </div>
    );
  }

  if (gone || removed) {
    return (
      <div className={styles.detail}>
        <div className={styles.connecting}>
          <p className={shared.threadNote}>This ticket is no longer available to you.</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={styles.detail}>
        <div className={styles.connecting}>
          {error ? (
            <p className={shared.error} role="alert">
              Couldn't load this ticket: {error}{" "}
              <button type="button" className={shared.linkButton} onClick={refetch}>
                Retry
              </button>
            </p>
          ) : (
            <p className={shared.threadNote}>Loading ticket…</p>
          )}
        </div>
      </div>
    );
  }

  const name = ticket.user_name || "Unknown user";
  const visible = ticketActions(ticket, viewerSub, fullAccess);
  const replying = canReply(ticket, viewerSub);
  // Only the owner and chat.support users can be in a direct room, so any
  // sender who isn't the owner is a supporter.
  const senderLabel = (sender: string, m: ChatMessage) =>
    m.sender_id === ticket.user_id ? sender : `${sender} · Support`;

  return (
    <div className={styles.detail}>
      <header className={styles.header}>
        <span
          className={styles.avatar}
          style={{ background: tintFor(ticket.user_id ?? ticket.id) }}
          aria-hidden="true"
        >
          {initials(name, "")}
        </span>
        <div className={styles.who}>
          <div className={styles.name}>{name}</div>
          <div className={styles.status}>{ticketStatusText(ticket, viewerSub)}</div>
        </div>
        <div className={styles.actions}>
          {ACTIONS.filter((a) => visible[a.key]).map((a) => (
            <button
              key={a.key}
              type="button"
              className={`${styles.action} ${styles[a.key]}`}
              onClick={() => void run(a)}
              disabled={busyHere}
            >
              {busyHere && busy?.action === a.key ? `${a.label}…` : a.label}
            </button>
          ))}
        </div>
      </header>

      {held ? (
        <ChatThread
          roomId={roomId}
          emptyText="No messages in this ticket yet."
          senderLabel={senderLabel}
        />
      ) : (
        <div className={styles.connecting}>
          {refused ? (
            <p className={shared.error} role="alert">
              Couldn't open this conversation.{" "}
              <button
                type="button"
                className={shared.linkButton}
                onClick={() => joinRoom(roomId, INBOX_HOLDER)}
              >
                Retry
              </button>
            </p>
          ) : (
            <p className={shared.threadNote}>Opening conversation…</p>
          )}
        </div>
      )}

      {replying ? (
        <ChatComposer roomId={roomId} inputId="ticket-input" placeholder={`Reply to ${name}…`} />
      ) : (
        <div className={shared.composerWrap}>
          <p className={`${shared.composerNotice} ${shared.info} ${styles.hint}`} role="status">
            <span>{composerHint(ticket, viewerSub, fullAccess)}</span>
          </p>
        </div>
      )}
    </div>
  );
}
